import { getPersonName, getNestedOrFloatingElementById } from '@fs/zion-sls'
import { getHeadOfHouseholdName } from '../../../householdList/utils/record.utils'

const getPersonDisplayName = (personId, images) => {
  const person = getNestedOrFloatingElementById(images, personId)
  return person ? getPersonName({ person }) : ''
}

const getHouseholdDisplayName = (destinationId, households, t) => {
  if (!destinationId) {
    return null
  }
  if (destinationId === 'new_record') {
    return t('edit-household.new-household.heading', 'New Household')
  }
  const record = households?.find((household) => household?.id === destinationId)
  const primaryPersonName = record && getHeadOfHouseholdName(record)
  return primaryPersonName
    ? t('edit-household.household-with-name', '{primaryPersonName} Household', { primaryPersonName })
    : t('edit-household.household', 'Household')
}

/**
 * Builds dnd-kit `accessibility.announcements` for the Edit Households drag and drop,
 * so screen reader users hear which person is being moved and which household is targeted.
 * @param {Object} params
 * @param {Array} params.households - The current households (records), used to resolve a drop target's display name.
 * @param {Array} params.images - The SLS images, used to resolve a dragged person's display name.
 * @param {Function} params.t - i18next translation function.
 * @returns {Object} dnd-kit `announcements` object (onDragStart, onDragOver, onDragEnd, onDragCancel).
 */
export const createHouseholdDragAnnouncements = ({ households, images, t }) => ({
  onDragStart: ({ active }) => {
    const personName = getPersonDisplayName(active?.id, images)
    return t('edit-household.dragAndDrop.dragStart', 'Draggable person {personName} selected.', { personName })
  },
  onDragOver: ({ active, over }) => {
    const personName = getPersonDisplayName(active?.id, images)
    const householdName = getHouseholdDisplayName(over?.id, households, t)

    if (householdName) {
      return t(
        'edit-household.dragAndDrop.dragOver',
        'Draggable person {personName} now over droppable {householdName} area.',
        { personName, householdName }
      )
    }

    return t(
      'edit-household.dragAndDrop.dragNotOver',
      'Draggable person {personName} no longer over a droppable area.',
      { personName }
    )
  },
  onDragEnd: ({ active, over }) => {
    const personName = getPersonDisplayName(active?.id, images)
    const householdName = getHouseholdDisplayName(over?.id, households, t)

    if (householdName) {
      return t(
        'edit-household.dragAndDrop.dragEnd',
        'Draggable person {personName} dropped into {householdName} area.',
        { personName, householdName }
      )
    }

    return t('edit-household.dragAndDrop.dragEndNotOver', 'Draggable person {personName} was dropped.', {
      personName,
    })
  },
  onDragCancel: ({ active }) => {
    const personName = getPersonDisplayName(active?.id, images)
    return t('edit-household.dragAndDrop.dragCancel', 'Draggable person {personName} released.', { personName })
  },
})

/**
 * Builds the screen-reader announcement for a failed save.
 *
 * The dnd-kit `onDragEnd` announcement above fires synchronously when the drag ends, before the
 * async save to SLS resolves, so it optimistically announces a successful drop even if the save
 * later fails. This message is announced separately, once the failure is known, to correct that
 * record for screen reader users.
 * @param {Object} params
 * @param {Object} params.person - The person whose move failed to save.
 * @param {Function} params.t - i18next translation function.
 * @returns {string} The announcement text.
 */
export const getDragErrorMessage = ({ person, t }) => {
  const personName = person ? getPersonName({ person }) : t('edit-household.dragAndDrop.unknownPerson', 'This person')
  return t(
    'edit-household.dragAndDrop.saveFailed',
    '{personName} was not moved. There was a problem saving the change.',
    { personName }
  )
}
